import Banner from "@components/features/product-list/banners/Banner";
import { useRouter } from "next/router";
import Breadcrumb from "@components/atoms/Breadcrumb";
import SubcategoriesSelected from "@components/features/product-list/filters/SubcategoriesSelected";
import SubcategoriesFilter from "@components/features/product-list/filters/SubcategoriesFilter";
import PriceFilter from "@components/features/product-list/filters/PriceFilter";
import BrandsFilter from "@components/features/product-list/filters/BrandsFilter";
import CateProducts from "@components/features/product-list/lists/CateProducts";
import {
  ProductAttributeFilter,
  ProductFilterLayer,
  ProductListMode,
  ProductSort,
  ProductSortBy,
} from "@interfaces/components/product-filters.interface";
import {
  LAYOUT_DEFAULT,
  PRODUCT_DEFAULT_PAGE,
  PRODUCT_DEFAULT_SORT_BY,
  PRODUCT_PAGE_SIZE,
  PRODUCT_SORT_LIST,
} from "@const/products.const";
import {
  useAttributes,
  useCategoryHeader,
  useCategoryRouteMapping,
} from "@api/products/categories";
import Loader from "@components/atoms/Loader";
import _ from "underscore";
import { useEffect, useState } from "react";
import { RangeValues } from "@interfaces/components/range-slide.interface";
import { Attribute } from "@interfaces/dto/categories.dto";
import { useProductKeyExchange, useProducts } from "@api/products/products";
import { Product } from "@interfaces/dto/products.dto";
import {
  DelimitedNumericArrayParam,
  StringParam,
  useQueryParam,
  withDefault,
} from "use-query-params";
import { extractRangeValuesFromString } from "@utils/numbers.util";
import { composeArrayToQueryParam } from "@utils/query.utils";
import {
  convertKeywordstoArray,
  convertRobotstoBoolean,
} from "@utils/seo.util";
import Skeletonbreadcrumbs from "@components/atoms/skeleton/ui-breadcrumbs";
import SkeletonFilterProduct from "@components/atoms/skeleton/ui-filter-product";
import SkeletonProductItem from "@components/atoms/skeleton/ui-product-item";

// SEO
import { NextSeo } from "next-seo";
import { SEO_SITEURL } from "@const/seo.const";
import { useSeoCategoryDetail } from "@api/seo/seo-page";
import CmsDetail from "@components/features/cms-page/CmsDetail";

const getProductAttributeFilters = ({
  attrs,
  layer,
}: {
  attrs: Attribute | undefined;
  layer: ProductFilterLayer;
}): ProductAttributeFilter[] => {
  const filteredAttrs = attrs?.items.find(
    ({ attr_code }) => attr_code === layer
  )?.values;
  return filteredAttrs ? filteredAttrs : [];
};

const getSelectedAttrs = ({
  selected,
  productAttrFilters,
}: {
  selected: number[];
  productAttrFilters: ProductAttributeFilter[];
}): ProductAttributeFilter[] => {
  const selectedProductAttrFiltered = productAttrFilters
    ? productAttrFilters.filter((subcat) =>
        selected.some((item) => item === subcat.value)
      )
    : [];
  return selectedProductAttrFiltered;
};

const ProductsPage = () => {
  const router = useRouter();
  // set attribute states
  const [selectedAttrSubcats, setSelectedAttrSubcats] = useQueryParam(
    "cat",
    withDefault(DelimitedNumericArrayParam, [])
  );
  const [selectedPriceRange, setSelectedPriceRange] = useQueryParam(
    "price",
    withDefault(StringParam, undefined)
  );
  const [selectedAttrBrands, setSelectedAttrBrands] = useQueryParam(
    "brand",
    withDefault(DelimitedNumericArrayParam, [])
  );

  // set paging states
  const [selectedPage, setSelectedPage] =
    useState<number>(PRODUCT_DEFAULT_PAGE);
  const [selectedSortBy, setSelectedSortBy] = useState<ProductSortBy>(
    PRODUCT_DEFAULT_SORT_BY
  );

  // set products render states
  const [selectedProductListMode, setSelectedProductListMode] = useQueryParam(
    "product_list_mode",
    StringParam
  );
  const [renderedProducts, setRenderedProducts] = useState<Product[]>([]);
  const [listKey, setListKey] = useState<number>(0); // generate incremental list key to refresh component
  const [orgPriceFilter, setOrgPriceFilter] = useState<RangeValues>();

  // get category mapping ID
  const { categories } = router.query;
  // add handler if categories[0] is "brands" or "search"

  const {
    data: dataProductKeyExchange,
    isLoading: isLoadingProductKeyExchange,
  } = useProductKeyExchange(categories ? categories[0] : undefined);
  if (
    !isLoadingProductKeyExchange &&
    dataProductKeyExchange?.type !== "category"
  ) {
    router.push("/404");
  }

  const {
    data: dataCatId,
    isLoading: isLoadingCatId,
    error: errorCatId,
  } = useCategoryRouteMapping({
    urlPath: categories
      ? composeArrayToQueryParam(categories as string[])
      : undefined,
  });

  // get category header
  const { data: dataCatHeader, isLoading: isLoadingCatHeader } =
    useCategoryHeader(dataCatId?.id);

  // get attributes
  const { data: dataAttr, isLoading: isLoadingAttr } = useAttributes({
    categoryId: dataCatId?.id,
    subcatIds: selectedAttrSubcats as number[],
    priceRange: selectedPriceRange,
    brandIds: selectedAttrBrands as number[],
  });

  // get SEO
  const { data: metaData, isLoading: isLoadingMetaData } = useSeoCategoryDetail(
    dataCatId?.id as string
  );
  const { noIndex, noFollow } = convertRobotstoBoolean(
    metaData?.robots as string
  );
  const keywords = convertKeywordstoArray(metaData?.keywords as string);

  // [subcat]
  const attrSubcats = getProductAttributeFilters({
    attrs: dataAttr,
    layer: "cat",
  });
  // [brand]
  const attrBrands = getProductAttributeFilters({
    attrs: dataAttr,
    layer: "brand",
  });
  // [display subcat]
  const displaySubcats = getSelectedAttrs({
    selected: selectedAttrSubcats as number[],
    productAttrFilters: attrSubcats,
  });
  // [price]
  const attrPrice = getProductAttributeFilters({
    attrs: dataAttr,
    layer: "price",
  });

  // get products
  const {
    data: dataProducts,
    isLoading: isLoadingProducts,
    error: errorProducts,
    mutate: mutateProducts,
  } = useProducts({
    categoryId: dataCatId?.id,
    subcatIds: selectedAttrSubcats as number[],
    priceRange: selectedPriceRange,
    brandIds: selectedAttrBrands as number[],
    productDisplayConfig: {
      pageSize: PRODUCT_PAGE_SIZE,
      currentPage: selectedPage,
      sortBy:
        selectedSortBy === "price_desc" || selectedSortBy === "price_asc"
          ? "price"
          : selectedSortBy,
      soryDirection:
        selectedSortBy === "price_desc" || selectedSortBy === "price_asc"
          ? (PRODUCT_SORT_LIST.find((sort) => sort.value === selectedSortBy)
              ?.desc as ProductSort)
          : undefined,
    },
  });

  // handle set org price range
  useEffect(() => {
    // init org price
    if (attrPrice.length > 0 && !orgPriceFilter) {
      const rangeValue = extractRangeValuesFromString(
        attrPrice[0].value.toString()
      );
      setOrgPriceFilter({
        start: Number(rangeValue?.start),
        end: Number(rangeValue?.end),
      });
    }
  }, [attrPrice, orgPriceFilter]);

  // Subscribe to route changes
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      // Handle route change event
      setSelectedPage(1);
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    // Clean up the event listener on unmount
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  // handle append products
  useEffect(() => {
    if (dataProducts?.items) {
      if (selectedPage === 1) {
        setRenderedProducts(dataProducts.items);
      } else {
        setRenderedProducts((prev) => [...prev, ...dataProducts.items]);
      }
    }
  }, [dataProducts?.items, selectedPage]);

  // handle invalid category
  if (errorCatId) {
    router.push("/404");
    return;
  }

  // handle invalid products
  if (errorProducts) {
    // TODO: handle when error from products reponse
  }

  // handle clear filters
  const handleClearFilters = () => {
    window.location.replace(
      `/${composeArrayToQueryParam(categories as string[])}`
    );
  };

  // handle product list mode
  const handleProductListMode = (mode: ProductListMode) => {
    setSelectedProductListMode(mode);
  };

  // handle reset page
  const handleResetPage = () => {
    setListKey((prev) => prev + 1);
    setSelectedPage(PRODUCT_DEFAULT_PAGE);
  };

  // handle subcat filter
  const handleSubcatFilter = (subcatId: number, isChecked: boolean) => {
    setSelectedAttrSubcats((prev) => {
      const prevSubcatFilters = prev as number[];
      if (isChecked) {
        return _.sortBy(_.uniq([...prevSubcatFilters, subcatId]));
      } else {
        return prevSubcatFilters.filter((subcat) => subcat !== subcatId);
      }
    });
    handleResetPage();
  };

  // handle price filter
  const handlePriceFilter = ({ start, end }: RangeValues) => {
    setSelectedPriceRange(`${start}-${end}`);
    handleResetPage();
  };

  // handle brand filter
  const handleBrandFilter = (brandId: number, isChecked: boolean) => {
    setSelectedAttrBrands((prev) => {
      const prevBrandFilters = prev as number[];
      if (isChecked) {
        return _.sortBy(_.uniq([...prevBrandFilters, brandId]));
      } else {
        return prevBrandFilters.filter((brand) => brand !== brandId);
      }
    });
    handleResetPage();
  };

  // handle sortby filter
  const handleSortByFilter = (sortBy: ProductSortBy) => {
    setSelectedSortBy(sortBy);
    handleResetPage();
  };

  // handle paging filter
  const handlePageFilter = (page: number) => {
    setSelectedPage(page);
  };

  // render loading
  if (isLoadingCatId) {
    return (
      <section className="mainContent">
        <div className="container-fluid mt-3">
          <Skeletonbreadcrumbs />
          <SkeletonProductItem />
        </div>
      </section>
    );
  }
  return (
    <>
      {/* SEO */}
      {metaData && (
        <NextSeo
          title={metaData?.title}
          description={metaData?.description}
          additionalMetaTags={keywords}
          noindex={noIndex}
          nofollow={noFollow}
          openGraph={{
            type: "object",
            url: `${SEO_SITEURL}/${composeArrayToQueryParam(
              categories as string[]
            )}`,
            title: metaData?.title,
            description: metaData?.description,
            images: [
              {
                url: metaData?.image ?? "",
              },
            ],
          }}
          canonical={`${SEO_SITEURL}/${composeArrayToQueryParam(
            categories as string[]
          )}`}
        />
      )}

      {isLoadingCatHeader ? (
        <div className="container-fluid">
          <Skeletonbreadcrumbs />
          <SkeletonProductItem />
        </div>
      ) : (
        <>
          {/* MAIN */}
          <section className="banner">
            <div className="container-fluid">
              {dataCatHeader && (
                <Banner
                  url={dataCatHeader.category.image}
                  title={dataCatHeader.category.name}
                />
              )}
            </div>
          </section>
          <section className="mainContent">
            <div className="container-fluid">
              <div className="row">
                <div className="col">
                  {dataCatHeader && (
                    <>
                      <h1>{dataCatHeader.category.name}</h1>
                      <div className="d-none d-lg-block">
                        <Breadcrumb
                          breadcrumbItems={dataCatHeader.breadcrumbs}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>
          <section className="product-section">
            <div className="container-fluid">
              <div className="d-flex">
                {dataCatHeader?.category.page_layout === LAYOUT_DEFAULT && (
                  <div className="sidebar-left filters offcanvas offcanvas-lg offcanvas-start">
                    <a
                      type="button"
                      className="btn-close"
                      data-bs-dismiss="offcanvas"
                    >
                      HIDE FILTER <i className="icon icon-close" />
                    </a>
                    {isLoadingAttr ? (
                      <SkeletonFilterProduct />
                    ) : (
                      displaySubcats.length > 0 && (
                        <SubcategoriesSelected
                          attributeFilters={displaySubcats}
                        />
                      )
                    )}
                    {isLoadingAttr ? (
                      <SkeletonFilterProduct />
                    ) : (
                      attrSubcats.length > 0 && (
                        <SubcategoriesFilter
                          attributeFilters={attrSubcats}
                          selectedAttributeFilters={
                            selectedAttrSubcats as number[]
                          }
                          onFilterChange={handleSubcatFilter}
                        />
                      )
                    )}
                    {orgPriceFilter && (
                      <PriceFilter
                        orgPriceRange={orgPriceFilter}
                        defaultPriceRange={extractRangeValuesFromString(
                          selectedPriceRange
                        )}
                        onFilterChange={handlePriceFilter}
                      />
                    )}
                    {isLoadingAttr ? (
                      <SkeletonFilterProduct />
                    ) : (
                      attrBrands.length > 0 && (
                        <BrandsFilter
                          attributeFilters={attrBrands}
                          selectedAttributeFilter={
                            selectedAttrBrands as number[]
                          }
                          onFilterChange={handleBrandFilter}
                        />
                      )
                    )}
                    <button
                      onClick={handleClearFilters}
                      className="btn btn-black w-100"
                      disabled={
                        selectedAttrSubcats.length === 0 &&
                        !selectedPriceRange &&
                        selectedAttrBrands.length === 0
                      }
                    >
                      ลบทั้งหมด
                    </button>
                  </div>
                )}

                {renderedProducts.length > 0 && (
                  <CateProducts
                    key={listKey}
                    productListMode={
                      selectedProductListMode
                        ? (selectedProductListMode as ProductListMode)
                        : "grid"
                    }
                    onViewModeChange={handleProductListMode}
                    products={renderedProducts}
                    page={selectedPage}
                    onPageChange={handlePageFilter}
                    sortBy={selectedSortBy}
                    onSortByChange={handleSortByFilter}
                    allowLoadMore={
                      !isLoadingProducts &&
                      renderedProducts.length >=
                        selectedPage * PRODUCT_PAGE_SIZE
                    }
                    pageLayout={dataCatHeader?.category.page_layout as string}
                  />
                )}
              </div>

              {/* Bottom Content */}
              {metaData && (
                <div className="bg-white">
                  <div className="container">
                    <div className="py-3">
                      <CmsDetail cmsDescHtml={metaData?.page_description} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        </>
      )}
    </>
  );
};

export default ProductsPage;
